import * as React from 'react';
import { Button, Input, Label, TextArea, TextField } from '@heroui/react';
import { IconPlus } from '@tabler/icons-react';
import { QuestionEditor } from './QuestionEditor';
import type { Question, QuestionnaireType, Section } from './types';

export interface SectionEditorProps {
  section: Section;
  sectionIndex?: number;
  questionnaireType?: QuestionnaireType;
  disabled?: boolean;
  onUpdateName: (name: string) => void;
  onUpdateDescription: (description: string) => void;
  onQuestionUpdate: (questionId: string, updates: Partial<Question>) => void;
  onQuestionRemove: (questionId: string) => void;
  onAddQuestion: () => void;
  onRemove: () => void;
}

export function SectionEditor({
  section,
  sectionIndex = 1,
  questionnaireType,
  disabled,
  onUpdateName,
  onUpdateDescription,
  onQuestionUpdate,
  onQuestionRemove,
  onAddQuestion,
  onRemove,
}: SectionEditorProps) {
  const sectionName = section.name?.trim() ? section.name : `第${sectionIndex}节`;

  return (
    <div className="space-y-4 rounded-lg border border-divider p-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-medium text-foreground">{sectionName}</h4>
        <Button type="button" variant="ghost" size="sm" onPress={onRemove} isDisabled={disabled}>
          删除章节
        </Button>
      </div>

      <TextField className="w-full" isDisabled={disabled} isRequired>
        <Label htmlFor={`section-name-${section.id}`}>
          章节名称<span className="text-danger ml-1">*</span>
        </Label>
        <Input
          id={`section-name-${section.id}`}
          value={section.name}
          onChange={(e) => onUpdateName(e.target.value)}
          placeholder="请输入章节名称"
        />
      </TextField>

      <TextField className="w-full" isDisabled={disabled}>
        <Label htmlFor={`section-description-${section.id}`}>章节说明</Label>
        <TextArea
          id={`section-description-${section.id}`}
          value={section.description ?? ''}
          onChange={(e) => onUpdateDescription(e.target.value)}
          placeholder="输入章节的说明"
          rows={2}
        />
      </TextField>

      <div className="space-y-4">
        {section.questions.map((question, index) => (
          <div key={question.id} className="border-t border-divider pt-4">
            <QuestionEditor
              question={question}
              questionIndex={index + 1}
              questionnaireType={questionnaireType}
              disabled={disabled}
              onUpdate={(u) => onQuestionUpdate(question.id, u)}
              onRemove={() => onQuestionRemove(question.id)}
            />
          </div>
        ))}
        <Button type="button" variant="secondary" size="sm" onPress={onAddQuestion} isDisabled={disabled} className="w-full">
          <IconPlus className="mr-2 size-4" />
          添加题目
        </Button>
      </div>
    </div>
  );
}
